import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPasswordPage() {
  const { branding } = useTenantBranding();
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>();

  async function onSubmit(values: ForgotPasswordFormValues) {
    setSubmitError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setSubmitError(
        'Une erreur est survenue. Vérifiez votre adresse email et réessayez.',
      );
      return;
    }

    setSent(true);
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cime-bg p-4">
      <div className="w-full max-w-[420px]">
        {/* Logo / Firm name */}
        <div className="text-center mb-8">
          <span
            className="text-xl font-bold text-cime-primary"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            {branding.firm_name}
          </span>
        </div>

        <div className="bg-cime-surface rounded-cime border border-neutral-200 shadow-sm p-8">
          {sent ? (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1
                className="text-xl font-bold text-cime-primary"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
              >
                Email envoyé
              </h1>
              <p className="text-sm text-neutral-600">
                Vérifiez votre boîte de réception et suivez le lien pour
                réinitialiser votre mot de passe.
              </p>
            </div>
          ) : (
            <>
              <h1
                className="text-xl font-bold text-cime-primary mb-1"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
              >
                Mot de passe oublié
              </h1>
              <p className="text-sm text-neutral-500 mb-6">
                Entrez votre adresse email pour recevoir un lien de
                réinitialisation.
              </p>

              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="space-y-4"
              >
                <Input
                  label="Adresse email"
                  type="email"
                  autoComplete="email"
                  placeholder="vous@cime-avocats.com"
                  error={errors.email?.message}
                  {...register('email', {
                    required: "L'adresse email est requise",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Adresse email invalide',
                    },
                  })}
                />

                {submitError ? (
                  <div
                    role="alert"
                    className="rounded-cime bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                  >
                    {submitError}
                  </div>
                ) : null}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isSubmitting}
                  className="w-full"
                >
                  Envoyer le lien de réinitialisation
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-cime-primary hover:underline"
            >
              <ArrowLeft className="w-3 h-3" aria-hidden />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
